import {
  ArrowLeft,
  ArrowRight,
  Award,
  BookOpen,
  CheckCircle2,
  ExternalLink,
  FileText,
  FolderGit2,
  Loader2,
  PlayCircle,
  Trophy,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  completeLearningNode,
  completeLearningResource,
  getAiGrowthTasks,
  getLatestAiGrowthPlan,
  getLearningNode,
  getLearningVideoRecommendations,
  getStudentGrowthMe,
  recordStudentGrowthEvent,
  updateAiGrowthTask,
} from '../../api.js'
import { buildStudentRoadmap, completeLearningStep } from '../../student-roadmap.js'
import { getEffectiveGrowthPlan, hasEffectiveGrowthData } from '../../student-growth-state.js'

function fallbackNode(nodeId) {
  return {
    id: nodeId,
    title: '学习节点',
    description: '当前节点暂时无法加载，请返回学习路线重新进入。',
    duration: '30 分钟',
    xp: 20,
    completed: false,
    stageTitle: '学习阶段',
    pathName: '学习成长路线',
    resources: [],
    practiceTasks: [],
  }
}

function searchResource(type, title, query) {
  const isVideo = type === 'video'
  return {
    id: `fallback-${type}-${encodeURIComponent(query)}`,
    type,
    title,
    platform: isVideo ? '哔哩哔哩' : '中国大学MOOC',
    description: isVideo ? '先选择一条结构清晰、评论反馈较好的中文讲解，重点看与本节点知识点对应的章节。' : '用于补充概念、课程章节和系统练习。',
    url: isVideo
      ? `https://search.bilibili.com/all?keyword=${encodeURIComponent(query)}`
      : `https://www.icourse163.org/search.htm?search=${encodeURIComponent(query)}`,
    completed: false,
  }
}

function localNodeFromGrowthPlan(payload, nodeId) {
  if (!hasEffectiveGrowthData(payload)) return null
  const plan = getEffectiveGrowthPlan(payload)
  const roadmap = buildStudentRoadmap(plan)

  for (const stage of roadmap) {
    const step = (stage.steps || []).find((item) => String(item.id) === String(nodeId))
    if (!step) continue

    const query = [step.title, ...(step.knowledgePoints || []).slice(0, 2)].filter(Boolean).join(' ')
    const resources = (step.resources || []).map((resource, index) => {
      if (typeof resource === 'string') {
        const isUrl = /^https?:\/\//i.test(resource)
        return {
          id: `${step.id}-resource-${index}`,
          type: isUrl && /bilibili|youku|iqiyi|mooc/i.test(resource) ? 'video' : 'document',
          title: isUrl ? `${step.title}学习资源` : resource,
          description: '',
          url: isUrl ? resource : '',
          completed: false,
        }
      }
      return {
        id: resource.id || `${step.id}-resource-${index}`,
        type: resource.type || (resource.platform === '哔哩哔哩' ? 'video' : 'document'),
        title: resource.title || resource.searchQuery || `${step.title}学习资源`,
        description: resource.description || resource.usage || '',
        platform: resource.platform || '',
        url: resource.url || '',
        completed: false,
      }
    })
    if (!resources.some((resource) => resource.type === 'video')) resources.unshift(searchResource('video', `${step.title}中文视频讲解`, query))
    if (!resources.some((resource) => resource.type === 'document')) resources.push(searchResource('document', `${step.title}系统课程与文档`, query))

    return {
      id: step.id,
      title: step.title,
      description: step.exercise || step.quiz || stage.goal,
      duration: `${step.minutes || 30} 分钟`,
      xp: step.xp || 20,
      completed: step.completed,
      stageTitle: stage.name,
      pathName: plan?.targetCareer || plan?.target || plan?.targetDirection || plan?.target_direction || plan?.direction || '学习成长路线',
      professionalGoal: step.professionalGoal || '',
      whyItMatters: step.whyItMatters || '',
      knowledgePoints: step.knowledgePoints || [],
      tools: step.tools || [],
      steps: step.steps || [],
      deliverables: step.deliverables || [],
      acceptanceCriteria: step.acceptanceCriteria || [],
      commonMistakes: step.commonMistakes || [],
      resources: plan?.source === 'ai_generated' ? resources : [
        {
          id: `${step.id}-doc`,
          type: 'document',
          title: step.doc || `${step.title} 学习笔记`,
          description: `整理 ${step.title} 的关键概念、步骤和常见问题。`,
          completed: false,
        },
        {
          id: `${step.id}-project`,
          type: 'project',
          title: step.project || '阶段作品',
          content: step.project || '把当前学习内容整理成可展示成果。',
          completed: false,
        },
      ],
      practiceTasks: plan?.source === 'ai_generated' ? [{
        id: `${step.id}-practice`,
        type: step.type === 'project' ? 'project_thinking' : 'short_answer',
        title: step.deliverables?.[0] || step.title,
        description: step.acceptanceCriteria?.join('；') || step.steps?.join('；'),
        difficulty: '专属任务',
        status: step.completed ? 'completed' : 'pending',
        bestScore: step.completed ? 100 : 0,
      }] : [
        {
          id: `${step.id}-practice`,
          type: 'short_answer',
          title: step.exercise || `${step.title} 小练习`,
          description: step.quiz || '用自己的话说明这个知识点，并记录一个应用场景。',
          difficulty: '基础',
          status: step.completed ? 'completed' : 'pending',
          bestScore: step.completed ? 100 : 0,
        },
      ],
      localPlan: plan,
      localStage: stage,
      localStep: step,
      source: 'growth_plan',
    }
  }

  return null
}

function practiceTypeLabel(type) {
  return {
    code: '代码题',
    short_answer: '简答题',
    project_thinking: '项目思考题',
    interview_question: '面试题',
  }[type] || '练习题'
}

function practiceStatusText(task) {
  if (task.status === 'completed' || task.completedAt) return '已完成'
  if (Number(task.attempts || 0) > 0) return '进行中'
  return '未开始'
}

function ResourceShell({ children, icon: Icon, tone, title, description, action }) {
  return (
    <article className={`learning-workbench-card tone-${tone}`}>
      <div className="learning-workbench-head">
        <span><Icon size={17} /> {title}</span>
        <p>{description}</p>
      </div>
      <div className="learning-workbench-body">{children}</div>
      {action ? <div className="learning-workbench-action">{action}</div> : null}
    </article>
  )
}

function LearningStepPage() {
  const { aiNodeId, stageId, idOrSlug } = useParams()
  const nodeId = aiNodeId || idOrSlug || stageId
  const isAiNode = Boolean(aiNodeId)
  const [node, setNode] = useState(() => fallbackNode(nodeId))
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [recommendedVideos, setRecommendedVideos] = useState([])
  const [videoLoading, setVideoLoading] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError('')
    const loadAiNode = async () => {
      const [{ plan }, taskPayload] = await Promise.all([
        getLatestAiGrowthPlan(controller.signal),
        getAiGrowthTasks(controller.signal).catch(() => ({ tasks: [] })),
      ])
      const localNode = localNodeFromGrowthPlan({ plan }, nodeId)
      if (!localNode) throw new Error('AI 学习节点不存在，请返回学习路线重新进入。')
      const aiTask = (taskPayload?.tasks || []).find((task) => String(task.nodeId) === String(nodeId))
      return { ...localNode, aiTaskId: aiTask?.id || null, completed: aiTask?.status === 'completed' || localNode.completed }
    }
    const requestNode = isAiNode
      ? loadAiNode()
      : getLearningNode(nodeId, controller.signal).then((payload) => payload?.node || fallbackNode(nodeId))
    requestNode
      .then((payload) => {
        setNode(payload || fallbackNode(nodeId))
      })
      .catch(async (err) => {
        if (err.name === 'AbortError') return
        try {
          const [aiPayload, growthPayload] = await Promise.all([
            getLatestAiGrowthPlan(controller.signal).catch(() => ({ plan: null })),
            getStudentGrowthMe(controller.signal).catch(() => null),
          ])
          const localNode = localNodeFromGrowthPlan(aiPayload?.plan ? { plan: aiPayload.plan } : growthPayload, nodeId)
          if (localNode) {
            setNode(localNode)
            setError('')
            return
          }
        } catch {
          // Keep the original learning-node error when no growth-plan fallback exists.
        }
        setNode(fallbackNode(nodeId))
        setError(err.message || '学习节点暂时加载失败，请稍后重试。')
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [isAiNode, nodeId])

  useEffect(() => {
    if (node.source !== 'growth_plan' || !node.title) return undefined
    const controller = new AbortController()
    const query = [node.title, ...(node.knowledgePoints || []).slice(0, 2)].filter(Boolean).join(' ')
    setVideoLoading(true)
    getLearningVideoRecommendations(query, controller.signal)
      .then((payload) => setRecommendedVideos(Array.isArray(payload?.videos) ? payload.videos : []))
      .catch((err) => {
        if (err.name !== 'AbortError') setRecommendedVideos([])
      })
      .finally(() => setVideoLoading(false))
    return () => controller.abort()
  }, [node.knowledgePoints, node.source, node.title])

  const resources = useMemo(() => (Array.isArray(node.resources) ? node.resources : []), [node.resources])
  const practiceTasks = useMemo(() => (Array.isArray(node.practiceTasks) ? node.practiceTasks : []), [node.practiceTasks])
  const planVideoResources = resources.filter((item) => item.type === 'video' && !String(item.url || '').includes('search.bilibili.com'))
  const videoResources = recommendedVideos.length ? recommendedVideos.map((video) => ({ ...video, completed: false })) : planVideoResources
  const documentResources = resources.filter((item) => item.type === 'document')
  const projectResources = resources.filter((item) => item.type === 'project')
  const competitions = resources.filter((item) => item.type === 'competition')
  const certificates = resources.filter((item) => item.type === 'certificate')
  const completedUnits = resources.filter((item) => item.completed).length
    + practiceTasks.filter((item) => item.status === 'completed' || item.completedAt).length
    + (node.completed ? 1 : 0)
  const totalUnits = resources.length + practiceTasks.length + 1
  const progress = totalUnits ? Math.round((completedUnits / totalUnits) * 100) : 0

  async function markResourceDone(resource) {
    if (!resource?.id || resource.completed || node.source === 'growth_plan') return
    try {
      await completeLearningResource(resource.id)
      setNode((current) => ({
        ...current,
        resources: (current.resources || []).map((item) => (
          item.id === resource.id ? { ...item, completed: true, completedAt: new Date().toISOString() } : item
        )),
      }))
    } catch {
      // External resources should still open even if progress recording fails.
    }
  }

  async function handleComplete() {
    if (node.completed) return
    setSaving(true)
    setNotice('')
    setError('')
    try {
      if (node.source === 'growth_plan' && node.localPlan && node.localStage && node.localStep) {
        if (node.aiTaskId) await updateAiGrowthTask(node.aiTaskId, { action: 'complete' })
        const result = completeLearningStep(node.localPlan, node.localStage, node.localStep)
        setNode((current) => ({
          ...current,
          completed: true,
          localStep: { ...current.localStep, completed: true },
          practiceTasks: (current.practiceTasks || []).map((task) => ({ ...task, status: 'completed', bestScore: 100 })),
        }))
        setNotice(`已完成节点，获得 ${node.xp || 20} XP。`)
        try {
          await recordStudentGrowthEvent({
            planId: node.localPlan?.id || undefined,
            eventType: 'learning_step_completed',
            title: `完成学习：${node.localStage.name} / ${node.localStep.title}`,
            description: node.localStep.exercise,
            metadata: {
              stageId: node.localStage.id,
              stepId: node.localStep.id,
              xp: node.localStep.xp || 20,
              stageCompleted: result.stageCompleted,
            },
          })
        } catch {
          // Local completion should still work when timeline recording is unavailable.
        }
        return
      }

      const payload = await completeLearningNode(node.id)
      setNode(payload?.node || { ...node, completed: true })
      setNotice(payload?.message || `已完成节点，获得 ${node.xp || 20} XP。`)
    } catch (err) {
      setError(err.message || '保存失败，请登录后重试。')
    } finally {
      setSaving(false)
    }
  }

  const firstVideo = videoResources[0]
  const firstDocument = documentResources[0]
  const firstProject = projectResources[0]

  return (
    <main className="page-container student-roadmap-page learning-node-product-page">
      <section className="student-roadmap-stage-toolbar">
        <Link className="secondary-button" to="/learning"><ArrowLeft size={16} /> 返回学习路线</Link>
        <Link className="secondary-button" to="/today">今日任务</Link>
      </section>

      <section className="learning-node-hero">
        <div>
          <span><BookOpen size={18} /> {node.pathName || '学习成长系统'} · {node.stageTitle || '学习节点'}</span>
          <h1>{loading ? '正在加载学习节点...' : node.title}</h1>
          <p>{node.description || '完成一个资源和一个练习后，再标记节点完成。'}</p>
        </div>
        <div className="learning-node-hero-metrics">
          <span>{node.duration || '30 分钟'}</span>
          <span>{node.xp || 20} XP</span>
          <span>{progress}% 完成</span>
        </div>
      </section>

      {error ? (
        <div className="student-growth-error">
          <strong>节点加载或保存失败</strong>
          <p>{error}</p>
          <Link className="secondary-button" to="/learning">返回学习路线</Link>
        </div>
      ) : null}
      {notice ? <p className="student-growth-error is-success">{notice}</p> : null}

      {node.source === 'growth_plan' ? (
        <section className="learning-node-guidance" aria-label="本节点学习指引">
          <div className="learning-node-guidance-copy">
            <span>现在怎么学</span>
            <h2>按 3 步完成本节点，不需要自己再整理路线</h2>
            <p>网站会帮你确定学习顺序、推荐中文资源、给出练习与交付标准，并把完成结果同步到成长进度。</p>
          </div>
          <ol>
            <li><strong>先建立概念</strong><span>打开推荐视频，先看与知识点对应的部分，不要求一次看完整门课程。</span></li>
            <li><strong>按步骤动手</strong><span>{node.steps?.[0] || '跟随执行步骤完成第一个可操作动作。'}</span></li>
            <li><strong>留下成果并验收</strong><span>{node.deliverables?.[0] || '保存练习结果或学习笔记，再对照验收标准检查。'}</span></li>
          </ol>
          {firstVideo?.url ? (
            <a className="primary-button" href={firstVideo.url} target="_blank" rel="noreferrer">开始第 1 步 <ExternalLink size={16} /></a>
          ) : null}
          <Link className="secondary-button" to="/ai-partner?intent=adjust-plan">遇到困难，让 AI 伙伴调整计划</Link>
        </section>
      ) : null}

      <section className="learning-node-summary-grid" aria-label="学习节点概览">
        <article className="learning-node-summary-card">
          <span>今日学习</span>
          <strong>{node.title || '学习节点'}</strong>
          <p>{node.description || '先完成一个资源和一个练习，再进入今日任务。'}</p>
          <Link
            className="primary-button"
            to={firstVideo?.url ? firstVideo.url : firstDocument?.id && node.source !== 'growth_plan' ? `/learning/resources/${firstDocument.id}` : '/today'}
            target={firstVideo?.url ? '_blank' : undefined}
            rel={firstVideo?.url ? 'noreferrer' : undefined}
          >
            开始学习 <ArrowRight size={16} />
          </Link>
        </article>
        <article className="learning-node-summary-card">
          <span>完成进度</span>
          <strong className="learning-node-progress-number">{progress}%</strong>
          <p>{completedUnits}/{totalUnits} 个学习动作已完成</p>
          <div className="learning-node-progress"><i style={{ width: `${progress}%` }} /></div>
        </article>
        <article className="learning-node-summary-card">
          <span>下一步建议</span>
          <strong>{node.completed ? '继续推进下一个节点' : '先完成一个资源和一个练习'}</strong>
          <p>完成后进入今日任务，或继续补充练习和项目成果。</p>
          <Link className="secondary-button" to="/today">查看今日任务</Link>
        </article>
      </section>

      {node.source === 'growth_plan' && node.professionalGoal ? (
        <section className="learning-workbench-card tone-document" aria-label="AI 专属节点要求">
          <div className="learning-workbench-head">
            <span><BookOpen size={17} /> 本节点的专业要求</span>
            <p>{node.whyItMatters}</p>
          </div>
          <div className="learning-workbench-body learning-node-professional-grid">
            <div><strong>专业目标</strong><p>{node.professionalGoal}</p></div>
            <div><strong>知识点</strong><p>{(node.knowledgePoints || []).join('、') || '以路线要求为准'}</p></div>
            <div><strong>工具</strong><p>{(node.tools || []).join('、') || '无需额外工具'}</p></div>
            <div><strong>执行步骤</strong><ol>{(node.steps || []).map((item) => <li key={item}>{item}</li>)}</ol></div>
            <div><strong>交付物</strong><ul>{(node.deliverables || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
            <div><strong>验收标准</strong><ul>{(node.acceptanceCriteria || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
            <div><strong>常见错误</strong><ul>{(node.commonMistakes || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
          </div>
        </section>
      ) : null}

      <section className="learning-workbench-grid" aria-label="学习资源工作台">
        <ResourceShell icon={PlayCircle} tone="video" title="视频资源" description="系统已按当前知识点匹配具体视频，直接观看，不需要再搜索。">
          {videoLoading ? <p className="learning-empty-note"><Loader2 size={16} /> 正在匹配具体视频...</p> : null}
          {videoResources.length ? videoResources.map((resource) => (
            <div className="learning-resource-row" key={resource.id || resource.title}>
              <div>
                <strong>{resource.title || '视频资源'}</strong>
                <p>{resource.platform || '国内学习平台'}{resource.author ? ` · UP主：${resource.author}` : ''}{resource.duration ? ` · ${resource.duration}` : ' · 建议先学 15-30 分钟'}</p>
                <small>{resource.reason || resource.description || resource.content || '观看后记录一个关键知识点。'}</small>
              </div>
              {resource.url ? (
                <a className="secondary-button" href={resource.url} target="_blank" rel="noreferrer" onClick={() => markResourceDone(resource)}>
                  开始观看 <ExternalLink size={15} />
                </a>
              ) : (
                <span className="learning-muted-action">暂无视频资源</span>
              )}
            </div>
          )) : !videoLoading ? <p className="learning-empty-note">暂时没有匹配到可验证的具体视频，系统不会用虚假链接代替。</p> : null}
        </ResourceShell>

        <ResourceShell icon={FileText} tone="document" title="文档资源" description="阅读文档，整理笔记和关键词。">
          {documentResources.length ? documentResources.map((resource) => (
            <div className="learning-resource-row" key={resource.id || resource.title}>
              <div>
                <strong>{resource.title || '学习文档'}</strong>
                <p>{resource.description || resource.summary || '围绕当前节点整理一页学习笔记。'}</p>
              </div>
              {node.source === 'growth_plan' ? (
                <span className="learning-muted-action">在本页完成</span>
              ) : (
                <Link className="secondary-button" to={`/learning/resources/${resource.id}`}>
                  阅读文档 <ArrowRight size={15} />
                </Link>
              )}
            </div>
          )) : <p className="learning-empty-note">暂无文档资源</p>}
        </ResourceShell>

        <ResourceShell icon={CheckCircle2} tone="practice" title="小练习" description="用练习检查是否真正理解。">
          {practiceTasks.length ? (
            <div className="learning-practice-card-list">
              {practiceTasks.map((task) => (
                <article className="learning-practice-card" key={task.id}>
                  <div>
                    <strong>{task.title || '练习题'}</strong>
                    <p>{task.question || task.description || '进入练习页查看要求。'}</p>
                  </div>
                  <div className="project-library-tags">
                    <small>{practiceTypeLabel(task.type)}</small>
                    <small>{task.difficulty || '基础'}</small>
                    <small>{practiceStatusText(task)}</small>
                    <small>最高分 {task.bestScore || 0}</small>
                  </div>
                  {node.source === 'growth_plan' && node.aiTaskId ? (
                    <Link className="secondary-button" to={`/learning/ai/${encodeURIComponent(node.id)}/exercise?taskId=${encodeURIComponent(node.aiTaskId)}`}>
                      进入 AI 问答练习 <ArrowRight size={16} />
                    </Link>
                  ) : node.source === 'growth_plan' ? (
                    <button className="secondary-button" type="button" onClick={handleComplete}>完成本练习 <ArrowRight size={16} /></button>
                  ) : (
                    <Link className="secondary-button" to={`/learning/practice/${task.id}`}>
                      开始练习 <ArrowRight size={16} />
                    </Link>
                  )}
                </article>
              ))}
            </div>
          ) : <p className="learning-empty-note">暂无练习</p>}
        </ResourceShell>

        <ResourceShell
          icon={FolderGit2}
          tone="project"
          title="推荐项目"
          description="把知识点沉淀成可展示成果。"
          action={<Link className="secondary-button" to={firstProject?.url || '/projects/recommendations'}>查看项目</Link>}
        >
          {projectResources.length ? projectResources.map((resource) => (
            <p key={resource.id || resource.title}><strong>{resource.title || '项目成长计划'}</strong>：{resource.metadata?.projectGoal || resource.content || '适合把当前节点知识转化成项目成果。'}</p>
          )) : <p className="learning-empty-note">暂无推荐项目，可先进入项目成长计划。</p>}
        </ResourceShell>

        <ResourceShell
          icon={Trophy}
          tone="competition"
          title="推荐竞赛"
          description="用阶段成果匹配竞赛机会。"
          action={<Link className="secondary-button" to="/more/competitions">查看竞赛</Link>}
        >
          {competitions.length ? competitions.map((resource) => <p key={resource.id || resource.title}>{resource.title}</p>) : <p className="learning-empty-note">暂无推荐竞赛</p>}
        </ResourceShell>

        <ResourceShell
          icon={Award}
          tone="certificate"
          title="推荐证书"
          description="补齐可以证明的能力标签。"
          action={<Link className="secondary-button" to="/more/certificates">查看证书</Link>}
        >
          {certificates.length ? certificates.map((resource) => <p key={resource.id || resource.title}>{resource.title}</p>) : <p className="learning-empty-note">暂无推荐证书</p>}
        </ResourceShell>
      </section>

      <section className="learning-node-complete-panel">
        <div>
          <span>节点完成</span>
          <h2>{node.completed ? '节点已完成' : '完成后写入成长中心'}</h2>
          <p>同一个节点只奖励一次 XP。完成后可以回到今日任务或继续练习。</p>
        </div>
        <button className="primary-button" type="button" disabled={saving || node.completed || loading} onClick={handleComplete}>
          {saving ? <Loader2 size={16} /> : <CheckCircle2 size={16} />}
          {node.completed ? '已完成' : '完成，获得 XP'}
        </button>
      </section>
    </main>
  )
}

export default LearningStepPage
